/**
 * CorroborationRules.jsx
 *
 * Editor for corroboration_rules.yaml + corroboration_outcomes.json +
 * corroboration_med_classes.json. Split out of the original
 * RuleEditorPage.jsx ("Corroboration Rules" tab).
 *
 * Body keeps the original 2-pane layout (rule list / editor) with three
 * sub-tabs inside the editor pane: Rules, Outcomes, Med Classes. The
 * condition tree builder, YAML preview, and per-state badges are
 * preserved. The outer shell (Sider + Header + Content with gradient
 * background, breadcrumb, Lock + Save buttons) follows the same
 * convention as the other configuration pages.
 *
 * Page lands locked (read-only). Click Lock → unlock → form fields,
 * condition-tree node operations, output type/value selects, and the
 * Reason input all become editable. The Outcomes and Med Classes
 * sub-tabs are read-only by nature. Save is a placeholder (no
 * persistence). All edits are in-memory.
 *
 * Loads from public/rules/ (served at /rules/):
 *   - corroboration_rules.yaml
 *   - corroboration_outcomes.json
 *   - corroboration_med_classes.json
 *
 * Dependency: js-yaml
 *
 * NOTE: `YAMLHighlight` and `ResizeHandle` are duplicated between this file
 * and CodeCorrectionRules.jsx. Easy to extract into a shared helpers
 * module later if desired.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Select, InputNumber,
  Spin, Alert, Tag, Table, message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined, SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const RULES_URL    = '/rules/corroboration_rules.yaml';
const OUTCOMES_URL = '/rules/corroboration_outcomes.json';
const MED_URL      = '/rules/corroboration_med_classes.json';


const EDITABLE_ROLES = ['hcc_reviewer'];


// ─── Design tokens (preserved from original RuleEditorPage) ─────────────
const C = {
  brand:        '#2a4a7c',
  brandDark:    '#162349',
  brandLight:   '#EDE9FE',
  border:       '#E5E7EB',
  gospel:       '#15803D',
  gospelBg:     '#F0FDF4',
  gospelBdr:    '#BBF7D0',
  // condition node colours
  andColor:     '#1D4ED8', andBg: '#EFF6FF', andBdr: '#BFDBFE',
  orColor:      '#D97706', orBg:  '#FFFBEB', orBdr:  '#FDE68A',
  presentColor: '#15803D', presentBg: '#F0FDF4', presentBdr: '#BBF7D0',
  absentColor:  '#B91C1C', absentBg:  '#FFF1F0', absentBdr:  '#FECACA',
  // state colours
  state1Color:  '#15803D', state1Bg: '#F0FDF4',
  state2Color:  '#B91C1C', state2Bg: '#FFF1F0',
};

const PREFIXES = ['PL', 'PMH', 'MED', 'ANYDX'];

// ─── Condition tree helpers ─────────────────────────────────────────────
let _uid = 0;
const genId = () => `n${++_uid}_${Math.random().toString(36).slice(2, 6)}`;

const parseToken = (token = '') => {
  const idx = token.indexOf('::');
  return idx === -1
    ? { prefix: 'PL', value: token }
    : { prefix: token.slice(0, idx), value: token.slice(idx + 2) };
};
const fmtToken = (prefix, value) => `${prefix}::${value}`;

function parseConditions(node) {
  if (!node) return { type: 'group', op: 'AND', id: genId(), children: [] };
  if (node.AND)     return { type: 'group', op: 'AND', id: genId(), children: (node.AND  || []).map(parseConditions) };
  if (node.OR)      return { type: 'group', op: 'OR',  id: genId(), children: (node.OR   || []).map(parseConditions) };
  if ('PRESENT' in node) return { type: 'leaf', op: 'PRESENT', id: genId(), token: node.PRESENT || '' };
  if ('ABSENT'  in node) return { type: 'leaf', op: 'ABSENT',  id: genId(), token: node.ABSENT  || '' };
  return { type: 'leaf', op: 'PRESENT', id: genId(), token: '' };
}

function serializeConditions(node) {
  if (!node) return {};
  if (node.type === 'group') return { [node.op]: (node.children || []).map(serializeConditions) };
  return { [node.op]: node.token };
}

function updateNode(tree, id, fn) {
  if (tree.id === id) return fn(tree);
  if (tree.type === 'group') return { ...tree, children: tree.children.map(c => updateNode(c, id, fn)) };
  return tree;
}
function removeNode(tree, id) {
  if (tree.type === 'group') {
    return { ...tree, children: tree.children.filter(c => c.id !== id).map(c => removeNode(c, id)) };
  }
  return tree;
}
function addChild(tree, groupId, child) {
  if (tree.id === groupId) return { ...tree, children: [...tree.children, child] };
  if (tree.type === 'group') return { ...tree, children: tree.children.map(c => addChild(c, groupId, child)) };
  return tree;
}

// ─── YAML preview (syntax highlighter) ──────────────────────────────────
const YAMLHighlight = ({ code }) => {
  if (!code) return null;
  const colorLine = (line) => {
    if (/^\s*#/.test(line)) return <span style={{ color: '#9CA3AF' }}>{line}</span>;
    const keyMatch = line.match(/^(\s*-?\s*)([^:]+)(:)(.*)/);
    if (keyMatch) {
      const [, indent, key, colon, rest] = keyMatch;
      const keyColor = /^(id|scope|priority|reason|type|value|code|confidence)$/.test(key.trim())
        ? '#7C3AED' : '#1D4ED8';
      const restColor = /^\s*(true|false)/.test(rest) ? '#D97706'
        : /^\s*\d/.test(rest) ? '#0369A1' : '#15803D';
      return (
        <>
          <span>{indent}</span>
          <span style={{ color: keyColor, fontWeight: 600 }}>{key}</span>
          <span style={{ color: '#6B7280' }}>{colon}</span>
          <span style={{ color: restColor }}>{rest}</span>
        </>
      );
    }
    if (/^\s*-\s+(AND|OR|PRESENT|ABSENT):/.test(line) || /^\s*(AND|OR|PRESENT|ABSENT):/.test(line)) {
      const opColors = { AND: C.andColor, OR: C.orColor, PRESENT: C.presentColor, ABSENT: C.absentColor };
      const matched = line.match(/(AND|OR|PRESENT|ABSENT)/);
      const op = matched?.[1];
      return <span style={{ color: opColors[op] || '#374151', fontWeight: 600 }}>{line}</span>;
    }
    return <span style={{ color: '#374151' }}>{line}</span>;
  };
  return (
    <pre style={{
      fontFamily: 'monospace', fontSize: 13, lineHeight: 1.65,
      background: '#FAFAFA', border: `1px solid ${C.border}`,
      borderRadius: 6, padding: '10px 14px',
      overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      margin: 0, flex: 1,
    }}>
      {code.split('\n').map((line, i) => <div key={i}>{colorLine(line)}</div>)}
    </pre>
  );
};

// ─── Resize handle ──────────────────────────────────────────────────────
const ResizeHandle = ({ onDrag }) => (
  <div
    onMouseDown={onDrag}
    style={{
      width: 6, flexShrink: 0, cursor: 'col-resize', background: C.border,
      position: 'relative', zIndex: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = C.brand + '55')}
    onMouseLeave={e => (e.currentTarget.style.background = C.border)}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none' }}>
      {[0,1,2,3,4].map(i => <div key={i} style={{ width: 2, height: 2, borderRadius: '50%', background: '#9CA3AF' }} />)}
    </div>
  </div>
);

// ─── PanelHeader ────────────────────────────────────────────────────────
const PanelHeader = ({ label, sub, right }) => (
  <div style={{
    padding: '7px 12px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  }}>
    <div>
      <Text style={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</Text>
      {sub && <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>{sub}</Text>}
    </div>
    {right}
  </div>
);

// ─── ConditionNode (recursive tree node) ────────────────────────────────
const ConditionNode = ({ node, onUpdate, onRemove, onAddChild, medClasses, editMode, depth = 0 }) => {
  const isGroup = node.type === 'group';
  const opMeta = {
    AND:     { color: C.andColor,     bg: C.andBg,     bdr: C.andBdr     },
    OR:      { color: C.orColor,      bg: C.orBg,      bdr: C.orBdr      },
    PRESENT: { color: C.presentColor, bg: C.presentBg, bdr: C.presentBdr },
    ABSENT:  { color: C.absentColor,  bg: C.absentBg,  bdr: C.absentBdr  },
  };
  const m = opMeta[node.op] || {};
  const { prefix, value } = isGroup ? {} : parseToken(node.token);

  return (
    <div style={{
      marginLeft: depth > 0 ? 20 : 0,
      marginTop: 6,
      borderLeft: depth > 0 ? `2px solid ${m.bdr || C.border}` : 'none',
      paddingLeft: depth > 0 ? 10 : 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '5px 8px',
        background: m.bg || '#F9FAFB',
        border: `1px solid ${m.bdr || C.border}`,
        borderRadius: 6,
      }}>
        {/* op selector */}
        <Select
          size="small"
          value={node.op}
          disabled={!editMode}
          onChange={val => onUpdate(node.id, n => ({ ...n, op: val, type: ['AND', 'OR'].includes(val) ? 'group' : 'leaf' }))}
          style={{ width: isGroup ? 80 : 100, fontWeight: 700, fontSize: 13 }}
          popupMatchSelectWidth={false}
        >
          <Option value="AND"><span style={{ color: C.andColor, fontWeight: 700 }}>AND</span></Option>
          <Option value="OR"><span style={{ color: C.orColor, fontWeight: 700 }}>OR</span></Option>
          <Option value="PRESENT"><span style={{ color: C.presentColor, fontWeight: 700 }}>PRESENT</span></Option>
          <Option value="ABSENT"><span style={{ color: C.absentColor, fontWeight: 700 }}>ABSENT</span></Option>
        </Select>

        {/* leaf token inputs */}
        {!isGroup && (
          <>
            <Select
              size="small"
              value={prefix}
              disabled={!editMode}
              onChange={val => onUpdate(node.id, n => ({ ...n, token: fmtToken(val, parseToken(n.token).value) }))}
              style={{ width: 86, fontSize: 13 }}
              popupMatchSelectWidth={false}
            >
              {PREFIXES.map(p => <Option key={p} value={p}>{p}</Option>)}
            </Select>
            <Text style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 700 }}>::</Text>
            {prefix === 'MED' ? (
              <Select
                size="small"
                showSearch
                value={value || undefined}
                disabled={!editMode}
                placeholder="Class…"
                onChange={val => onUpdate(node.id, n => ({ ...n, token: fmtToken(parseToken(n.token).prefix, val) }))}
                style={{ minWidth: 160, fontSize: 13 }}
                popupMatchSelectWidth={false}
                filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              >
                {medClasses.map(cls => <Option key={cls} value={cls}>{cls}</Option>)}
              </Select>
            ) : (
              <Input
                size="small"
                value={value}
                readOnly={!editMode}
                placeholder="e.g. E11 or I48.0"
                onChange={e => onUpdate(node.id, n => ({ ...n, token: fmtToken(parseToken(n.token).prefix, e.target.value) }))}
                style={{ width: 130, fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}
              />
            )}
          </>
        )}

        {/* group label */}
        {isGroup && (
          <Text style={{ color: m.color, fontSize: 12, fontWeight: 600 }}>
            {node.children?.length || 0} condition{node.children?.length !== 1 ? 's' : ''}
          </Text>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {/* add child (group only) — edit mode only */}
          {isGroup && editMode && (
            <>
              <Tooltip title="Add condition">
                <Button
                  size="small" icon={<PlusOutlined />}
                  onClick={() => onAddChild(node.id, { type: 'leaf', op: 'PRESENT', id: genId(), token: 'PL::' })}
                  style={{ fontSize: 12, color: C.brand, borderColor: C.brand }}
                />
              </Tooltip>
              <Tooltip title="Add group">
                <Button
                  size="small"
                  onClick={() => onAddChild(node.id, { type: 'group', op: 'AND', id: genId(), children: [] })}
                  style={{ fontSize: 12, color: C.andColor, borderColor: C.andColor }}
                >
                  + Group
                </Button>
              </Tooltip>
            </>
          )}
          {/* remove — edit mode only, not root */}
          {depth > 0 && editMode && (
            <Tooltip title="Remove">
              <Button
                size="small" danger icon={<DeleteOutlined />}
                onClick={() => onRemove(node.id)}
                style={{ fontSize: 12 }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      {/* children */}
      {isGroup && node.children?.map(child => (
        <ConditionNode
          key={child.id}
          node={child}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onAddChild={onAddChild}
          medClasses={medClasses}
          editMode={editMode}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

// ─── Corroboration Rule List ────────────────────────────────────────────
const CorrRuleList = ({ rules, outcomes, selectedId, onSelect }) => {
  const sorted = useMemo(() => [...(rules || [])].sort((a, b) => a.priority - b.priority), [rules]);
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {sorted.map(r => {
        const isActive   = r.id === selectedId;
        const isHallucin = r.output?.reason?.startsWith('hallucination_prevention');
        const hasOutcome = !!outcomes?.[r.id];
        const state = isHallucin ? 2 : hasOutcome ? 1 : 3;
        const stateMeta = {
          1: { label: 'S1', color: C.state1Color, bg: C.state1Bg },
          2: { label: 'S2', color: C.absentColor, bg: C.absentBg },
          3: { label: 'S3', color: '#D97706',     bg: '#FFFBEB' },
        }[state];

        return (
          <div
            key={r.id}
            onClick={() => onSelect(r)}
            style={{
              padding: '9px 12px', cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              borderLeft: `3px solid ${isActive ? C.brand : 'transparent'}`,
              background: isActive ? C.brandLight : 'transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: isActive ? C.brand : '#E5E7EB',
                color: isActive ? '#fff' : '#6B7280', fontFamily: 'monospace',
              }}>{r.priority}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: stateMeta.bg, color: stateMeta.color,
              }}>{stateMeta.label}</span>
              {hasOutcome && (
                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: C.state1Color }}>
                  {'-> '}{outcomes[r.id].code}
                </span>
              )}
            </div>
            <Text style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: '#111827', display: 'block', marginTop: 3, lineHeight: '15px' }}>
              {r.id}
            </Text>
            <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
              {(r.tags || []).slice(0, 3).map(t => (
                <span key={t} style={{ fontSize: 11, padding: '0 5px', borderRadius: 9999, background: '#F3F4F6', color: '#6B7280', border: `1px solid ${C.border}` }}>{t}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Corroboration Rule Editor ──────────────────────────────────────────
const CorrRuleEditor = ({ rule, outcomes, onChange, medClasses, editMode }) => {
  const condTree = rule?._condTree ?? null;

  const setCondTree = useCallback((newTree) => {
    if (!rule) return;
    onChange({ ...rule, _condTree: newTree });
  }, [rule, onChange]);

  const handleUpdate   = useCallback((id, fn) => setCondTree(updateNode(condTree, id, fn)),    [condTree, setCondTree]);
  const handleRemove   = useCallback((id)      => setCondTree(removeNode(condTree, id)),        [condTree, setCondTree]);
  const handleAddChild = useCallback((gId, ch) => setCondTree(addChild(condTree, gId, ch)),     [condTree, setCondTree]);

  const yamlPreview = useMemo(() => {
    if (!rule) return '';
    const obj = {
      id: rule.id,
      priority: rule.priority,
      description: rule.description,
      effective_from: rule.effective_from,
      tags: rule.tags,
      conditions: serializeConditions(condTree),
      output: rule.output,
    };
    try { return yaml.dump(obj, { lineWidth: 56, indent: 2 }); }
    catch { return '(error generating YAML)'; }
  }, [rule, condTree]);

  if (!rule) return (
    <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#9CA3AF', fontSize: 15 }}>Select a rule to edit</Text>
    </div>
  );

  const isHallucin = rule.output?.reason?.startsWith('hallucination_prevention');
  const outcome    = outcomes?.[rule.id];
  const state      = isHallucin ? 2 : outcome ? 1 : 3;

  const stateBadge = (
    <span style={{
      fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: state === 1 ? C.state1Bg : state === 2 ? C.absentBg : '#FFFBEB',
      color: state === 1 ? C.state1Color : state === 2 ? C.absentColor : '#D97706',
      border: `1px solid ${state === 1 ? C.gospelBdr : state === 2 ? '#FECACA' : '#FDE68A'}`,
    }}>
      {state === 1 ? '>> State 1 — Inject' : state === 2 ? 'BLOCK State 2 — Block LLM' : '? State 3 — LLM Fallback'}
    </span>
  );

  const labelStyle = { fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left: form + condition builder */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', overflowY: 'auto', flex: 1 }}>

          {/* id / priority / state */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 2 }}>
              <Text style={labelStyle}>Rule ID</Text>
              <Input value={rule.id} readOnly
                style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, background: '#F9FAFB' }} />
            </div>
            <div style={{ flex: '0 0 80px' }}>
              <Text style={labelStyle}>Priority</Text>
              <InputNumber
                value={rule.priority} min={1} max={999}
                disabled={!editMode}
                onChange={val => onChange({ ...rule, priority: val })}
                style={{ width: '100%', fontSize: 13 }}
              />
            </div>
            <div style={{ flex: '0 0 140px', paddingTop: 22 }}>{stateBadge}</div>
          </div>

          {/* description */}
          <div style={{ marginBottom: 12 }}>
            <Text style={labelStyle}>Description</Text>
            <TextArea
              value={rule.description}
              onChange={e => onChange({ ...rule, description: e.target.value })}
              readOnly={!editMode}
              rows={2} style={{ fontSize: 13 }}
            />
          </div>

          {/* tags */}
          <div style={{ marginBottom: 14 }}>
            <Text style={labelStyle}>Tags</Text>
            <Select
              mode="tags" style={{ width: '100%', fontSize: 13 }}
              value={rule.tags || []}
              disabled={!editMode}
              onChange={vals => onChange({ ...rule, tags: vals })}
              tokenSeparators={[',']}
              placeholder="e.g. diabetes, cardiac, hcc_37"
            />
          </div>

          {/* Condition tree */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text style={labelStyle}>Conditions</Text>
              <Text style={{ fontSize: 12, color: '#9CA3AF' }}>PL / PMH / MED / ANYDX</Text>
            </div>
            <div style={{
              background: '#FAFAFA', border: `1px solid ${C.border}`,
              borderRadius: 7, padding: '10px 10px 10px 8px',
            }}>
              {condTree ? (
                <ConditionNode
                  node={condTree}
                  onUpdate={handleUpdate}
                  onRemove={handleRemove}
                  onAddChild={handleAddChild}
                  medClasses={medClasses}
                  editMode={editMode}
                  depth={0}
                />
              ) : (
                editMode ? (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setCondTree({ type: 'group', op: 'AND', id: genId(), children: [] })}
                    style={{ color: C.brand, borderColor: C.brand, borderStyle: 'dashed' }}
                  >
                    Add root condition
                  </Button>
                ) : (
                  <Text style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic' }}>
                    No conditions. Unlock to add.
                  </Text>
                )
              )}
            </div>
          </div>

          {/* Output */}
          <div style={{
            padding: '10px 12px',
            background: state === 1 ? C.state1Bg : state === 2 ? C.absentBg : '#FFFBEB',
            border: `1px solid ${state === 1 ? C.gospelBdr : state === 2 ? '#FECACA' : '#FDE68A'}`,
            borderRadius: 7,
          }}>
            <Text style={{ ...labelStyle, marginBottom: 8 }}>Output</Text>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Type</Text>
                <Select
                  value={rule.output?.type || 'BOOLEAN'}
                  disabled={!editMode}
                  style={{ width: 110, fontSize: 13 }}
                  onChange={val => onChange({ ...rule, output: { ...rule.output, type: val } })}
                >
                  <Option value="BOOLEAN">BOOLEAN</Option>
                </Select>
              </div>
              <div>
                <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Value</Text>
                <Select
                  value={String(rule.output?.value ?? true)}
                  disabled={!editMode}
                  style={{ width: 80, fontSize: 13 }}
                  onChange={val => onChange({ ...rule, output: { ...rule.output, value: val === 'true' } })}
                >
                  <Option value="true">true</Option>
                  <Option value="false">false</Option>
                </Select>
              </div>
              {outcome && (
                <div>
                  <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Injects code</Text>
                  <span style={{
                    fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: C.state1Color,
                    background: '#fff', border: `1px solid ${C.gospelBdr}`, borderRadius: 4, padding: '3px 8px',
                  }}>
                    {outcome.code}
                  </span>
                  <span style={{ marginLeft: 6, fontSize: 12, color: '#6B7280' }}>conf: {outcome.confidence}</span>
                </div>
              )}
              {isHallucin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <WarningOutlined style={{ color: C.absentColor, fontSize: 15 }} />
                  <Text style={{ fontSize: 13, color: C.absentColor, fontWeight: 600 }}>LLM Suppressed — no code injected</Text>
                </div>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Reason</Text>
              <Input
                value={rule.output?.reason || ''}
                onChange={e => onChange({ ...rule, output: { ...rule.output, reason: e.target.value } })}
                readOnly={!editMode}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                placeholder="Audit reason string (prefix with 'hallucination_prevention:' for State 2)"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div
        style={{ width: 6, flexShrink: 0, background: C.border }}
        onMouseEnter={e => (e.currentTarget.style.background = C.brand + '55')}
        onMouseLeave={e => (e.currentTarget.style.background = C.border)}
      />

      {/* Right: YAML preview */}
      <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{
          padding: '7px 12px', borderBottom: `1px solid ${C.border}`,
          background: '#F5F3FF', flexShrink: 0,
        }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            YAML Preview
          </Text>
          <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>
            Live — updates as you edit
          </Text>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          <YAMLHighlight code={yamlPreview} />
        </div>
      </div>
    </div>
  );
};

// ─── Outcomes Table ─────────────────────────────────────────────────────
const OutcomesTable = ({ outcomes }) => {
  const data = useMemo(() =>
    Object.entries(outcomes || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([id, v]) => ({ key: id, rule_id: id, code: v.code, confidence: v.confidence })),
  [outcomes]);

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
          Outcomes — {data.length} entries
        </Text>
        <Text style={{ fontSize: 13, color: '#9CA3AF' }}>
          State 1 rules only · State 2 rules have no entry by design
        </Text>
      </div>
      <Table
        dataSource={data}
        size="small"
        pagination={false}
        rowKey="key"
        columns={[
          {
            title: 'Rule ID',
            dataIndex: 'rule_id',
            width: 320,
            render: v => <Text style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{v}</Text>,
          },
          {
            title: 'Injected Code',
            dataIndex: 'code',
            width: 140,
            render: v => (
              <span style={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: C.state1Color,
                background: C.gospelBg, border: `1px solid ${C.gospelBdr}`, borderRadius: 4, padding: '2px 8px',
              }}>
                {v}
              </span>
            ),
          },
          {
            title: 'Confidence',
            dataIndex: 'confidence',
            width: 120,
            render: v => {
              const pct = Math.round(v * 100);
              const color = pct >= 88 ? C.state1Color : pct >= 80 ? '#D97706' : C.absentColor;
              return <Text style={{ fontSize: 14, fontWeight: 700, color }}>{pct}%</Text>;
            },
          },
        ]}
        style={{ fontSize: 13 }}
      />
    </div>
  );
};

// ─── Med Classes Table ──────────────────────────────────────────────────
const MedClassTable = ({ medClasses }) => {
  const [search, setSearch] = useState('');

  const data = useMemo(() => {
    const entries = Object.entries(medClasses || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([drug, cls]) => ({ key: drug, drug, cls }));
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter(e => e.drug.includes(q) || e.cls.toLowerCase().includes(q));
  }, [medClasses, search]);

  const uniqueClasses = useMemo(() =>
    [...new Set(Object.entries(medClasses || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([, v]) => v))].sort(),
  [medClasses]);

  const classColors = ['blue','purple','green','orange','red','cyan','magenta','gold','lime','geekblue'];
  const classColorMap = useMemo(() => {
    const map = {};
    uniqueClasses.forEach((cls, i) => { map[cls] = classColors[i % classColors.length]; });
    return map;
  }, [uniqueClasses]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
          placeholder="Search drug name or class…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 300, fontSize: 14 }}
          allowClear
        />
        <Text style={{ fontSize: 13, color: '#6B7280' }}>
          {data.length} entries · {uniqueClasses.length} classes
        </Text>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {uniqueClasses.map(cls => (
            <Tag
              key={cls}
              color={classColorMap[cls]}
              style={{ fontSize: 12, cursor: 'pointer' }}
              onClick={() => setSearch(cls)}
            >
              {cls}
            </Tag>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <Table
          dataSource={data}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `${t} entries` }}
          rowKey="key"
          columns={[
            {
              title: 'Drug Name',
              dataIndex: 'drug',
              width: 240,
              render: v => <Text style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>{v}</Text>,
              sorter: (a, b) => a.drug.localeCompare(b.drug),
            },
            {
              title: 'MED:: Class Token',
              dataIndex: 'cls',
              render: v => <Tag color={classColorMap[v]} style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>{v}</Tag>,
              filters: uniqueClasses.map(c => ({ text: c, value: c })),
              onFilter: (val, rec) => rec.cls === val,
              sorter: (a, b) => a.cls.localeCompare(b.cls),
            },
          ]}
        />
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────
const CorroborationRules = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [corrData, setCorrData]     = useState(null);
  const [outcomes, setOutcomes]     = useState(null);
  const [medClasses, setMedClasses] = useState(null);
  const [selCorrRule, setSelCorrRule] = useState(null);
  const [corrSubTab, setCorrSubTab] = useState('rules');
  const [editMode, setEditMode]     = useState(false);  // false = locked
  const [listW, setListW]           = useState(280);

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Load all 3 files ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadFile = (path, format = 'json') =>
      fetch(path).then(r => { if (!r.ok) throw new Error(path); return r.text(); })
        .then(t => format === 'yaml' ? yaml.load(t) : JSON.parse(t));

    Promise.allSettled([
      loadFile(RULES_URL, 'yaml'),
      loadFile(OUTCOMES_URL),
      loadFile(MED_URL),
    ]).then(([corr, out, med]) => {
      if (cancelled) return;

      // Track which essential files failed (rules YAML is the critical one)
      if (corr.status === 'rejected') {
        console.error('Failed to load corroboration_rules.yaml:', corr.reason);
        setLoadError(`Could not load ${RULES_URL}`);
      }

      if (corr.status === 'fulfilled') {
        const rulesWithTree = (corr.value?.rules || []).map(r => ({
          ...r,
          _condTree: parseConditions(r.conditions),
        }));
        setCorrData({ ...corr.value, rules: rulesWithTree });
        if (rulesWithTree.length) setSelCorrRule(rulesWithTree[0]);
      }
      if (out.status === 'fulfilled') setOutcomes(out.value);
      if (med.status === 'fulfilled') setMedClasses(med.value);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // ── Resize drag ──────────────────────────────────────────────────
  const startDrag = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX, base = listW;
    const onMove = me => setListW(Math.max(200, base + (me.clientX - startX)));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [listW]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);
  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const handleRuleChange = (updated) => {
    setSelCorrRule(updated);
    setCorrData(prev => ({
      ...prev,
      rules: prev.rules.map(x => x.id === updated.id ? updated : x),
    }));
  };

  // ── Derived ──────────────────────────────────────────────────────
  const corrRules = corrData?.rules || [];

  const medClassOptions = useMemo(() =>
    [...new Set(Object.entries(medClasses || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([, v]) => v))].sort(),
  [medClasses]);

  const stats = useMemo(() => ({
    total: corrRules.length,
    state1: corrRules.filter(r => !r.output?.reason?.startsWith('hallucination') && outcomes?.[r.id]).length,
    state2: corrRules.filter(r => r.output?.reason?.startsWith('hallucination')).length,
    state3: corrRules.filter(r => !outcomes?.[r.id] && !r.output?.reason?.startsWith('hallucination')).length,
  }), [corrRules, outcomes]);

  // ── Sub-tab styling ──────────────────────────────────────────────
  const subTabStyle = (key) => ({
    padding: '4px 14px',
    fontSize: 13,
    fontWeight: corrSubTab === key ? 700 : 400,
    cursor: 'pointer',
    color: corrSubTab === key ? C.brand : '#6B7280',
    background: corrSubTab === key ? C.brandLight : 'transparent',
    borderRadius: 6,
    border: corrSubTab === key ? `1px solid #DDD6FE` : '1px solid transparent',
    userSelect: 'none',
  });

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider ──────────────────────────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'fixed',
          left: 0, top: 0, bottom: 0, zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column', justifyContent: 'space-between',
          height: '100%', alignItems: 'center', padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white', fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Corroboration Rules
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>


        </Header>

        {/* ── Content ───────────────────────────────────────────── */}
        <Content style={{
          padding: 0,
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex', flexDirection: 'column',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading corroboration rules…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={<span>{loadError}</span>}
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && corrData && (
            <>
              {/* Stats strip */}
              <div style={{
                padding: '10px 20px',
                background: 'rgba(255,255,255,0.6)',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 24,
                flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.state1Color, lineHeight: '20px' }}>{stats.state1}</div>
                  <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 1</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.absentColor, lineHeight: '20px' }}>{stats.state2}</div>
                  <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 2</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#D97706', lineHeight: '20px' }}>{stats.state3}</div>
                  <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 3</div>
                </div>
                <div style={{ flex: 1 }} />
                <Text style={{ fontSize: 12, color: '#6B7280' }}>
                  {corrData.namespace} · v{corrData.version}
                </Text>
              </div>

              {/* Editor body */}
              <div style={{
                flex: 1, display: 'flex', overflow: 'hidden',
                background: 'white',
                margin: 16,
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}>

                {/* P1: Rule list (Rules sub-tab only) */}
                {corrSubTab === 'rules' && (
                  <>
                    <div style={{
                      width: listW, flexShrink: 0,
                      display: 'flex', flexDirection: 'column',
                      background: '#FAFAFA',
                      overflow: 'hidden',
                      borderTopLeftRadius: 8, borderBottomLeftRadius: 8,
                    }}>
                      <PanelHeader
                        label="Rules"
                        sub={`${corrData?.namespace} · v${corrData?.version}`}
                      />
                      <CorrRuleList
                        rules={corrRules}
                        outcomes={outcomes}
                        selectedId={selCorrRule?.id}
                        onSelect={r => setSelCorrRule(r)}
                      />
                    </div>
                    <ResizeHandle onDrag={startDrag} />
                  </>
                )}

                {/* P2: main area */}
                <div style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden', background: '#fff',
                  borderTopRightRadius: 8, borderBottomRightRadius: 8,
                  borderTopLeftRadius: corrSubTab !== 'rules' ? 8 : 0,
                  borderBottomLeftRadius: corrSubTab !== 'rules' ? 8 : 0,
                }}>

                  {/* sub-tab bar */}
                  <div style={{
                    padding: '8px 14px', borderBottom: `1px solid ${C.border}`,
                    background: '#F9FAFB',
                    display: 'flex', alignItems: 'center', gap: 6,
                    flexShrink: 0,
                  }}>
                    {[
                      { key: 'rules',      label: 'Rules',       count: corrRules.length },
                      { key: 'outcomes',   label: 'Outcomes',    count: Object.keys(outcomes || {}).filter(k => !k.startsWith('_')).length },
                      { key: 'medclasses', label: 'Med Classes', count: Object.keys(medClasses || {}).filter(k => !k.startsWith('_')).length },
                    ].map(({ key, label, count }) => (
                      <div key={key} onClick={() => setCorrSubTab(key)} style={subTabStyle(key)}>
                        {label}
                        <span style={{
                          marginLeft: 5, fontSize: 11, fontWeight: 700, padding: '0 5px', borderRadius: 9999,
                          background: corrSubTab === key ? C.brand : '#E5E7EB',
                          color: corrSubTab === key ? '#fff' : '#6B7280',
                        }}>
                          {count}
                        </span>
                      </div>
                    ))}
                    {corrSubTab === 'rules' && selCorrRule && (
                      <>
                        <div style={{ flex: 1 }} />
                        <Text style={{ fontSize: 13, color: '#9CA3AF' }}>{selCorrRule.id}</Text>
                      </>
                    )}
                  </div>

                  {/* sub-tab content */}
                  <div style={{
                    flex: 1, display: 'flex', overflow: 'hidden',
                    userSelect: corrSubTab === 'rules' ? 'none' : 'text',
                  }}>
                    {corrSubTab === 'rules' && (
                      <CorrRuleEditor
                        rule={selCorrRule}
                        outcomes={outcomes}
                        onChange={handleRuleChange}
                        medClasses={medClassOptions}
                        editMode={editMode}
                      />
                    )}
                    {corrSubTab === 'outcomes'   && <OutcomesTable  outcomes={outcomes} />}
                    {corrSubTab === 'medclasses' && <MedClassTable medClasses={medClasses} />}
                  </div>
                </div>
              </div>
            </>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

export default CorroborationRules;